// Shared formatting/derivation helpers for the Field Services Snapshot Report.
// Used by both the printable (PDF) layout and the .docx builder so the two
// documents always agree on values.

const DASH = "—";

// The blank-template payload (blankSnapshotData) pre-fills fields with
// literal strings ("Yes / No", fill-in lines), so the value formatters all
// pass non-date strings straight through.

export const yn = (value) => {
  if (typeof value === "string") return value;
  return value === true ? "Yes" : value === false ? "No" : DASH;
};

export const fmtText = (value) =>
  value === null || value === undefined || value === "" ? DASH : String(value);

export const fmtNum = (value, suffix = "") => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined || isNaN(value)) return DASH;
  const rounded = Math.round(value * 100) / 100;
  return `${rounded}${suffix}`;
};

// inspection_date arrives as a plain "yyyy-MM-dd" string; format it without
// constructing a Date (a UTC parse would render it a day early).
export const fmtDateString = (value) => {
  if (!value) return DASH;
  if (typeof value === "string" && !/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value;
  }
  const [y, m, d] = String(value).slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
};

export const fmtTimestamp = (value) => {
  if (!value) return DASH;
  const date = new Date(value);
  if (isNaN(date)) return DASH;
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const fmtTime = (value) => {
  if (!value) return DASH;
  const date = new Date(value);
  if (isNaN(date)) return DASH;
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

// Matches the Water Levels entry form: final DTW = reading − MP height (in→ft)
export const finalDtw = (valFt, mpHeightIn) => {
  if (valFt === null || valFt === undefined || isNaN(valFt)) return null;
  return valFt - (mpHeightIn || 0) / 12;
};

export const fmtGps = (latDd, lonDd) => {
  if (typeof latDd === "string") return latDd;
  if (latDd === null || latDd === undefined) return DASH;
  const lat = Math.abs(latDd).toFixed(6);
  const lon = Math.abs(lonDd ?? 0).toFixed(6);
  return `N ${lat}°  W ${lon}°`;
};

export const INSPECTION_CHECKLIST = [
  { field: "chk_identity", label: "Identity" },
  { field: "chk_location", label: "Location" },
  { field: "chk_use", label: "Use" },
  { field: "chk_setbacks", label: "Setbacks" },
  { field: "chk_meter", label: "Meter" },
  { field: "chk_equipment", label: "Equipment" },
  { field: "chk_treatment", label: "Treatment" },
  { field: "chk_surf_compl", label: "Surface Completion" },
  { field: "chk_classification", label: "Classification" },
];

// Setback rows exactly as they appear on the paper field sheet. "actual" pulls
// from the well record; septic / active wells / property line are numeric,
// contamination sources / critical environmental features are free text.
export const setbackRows = (inspection) => [
  {
    label: "Septic System",
    required: "150",
    actual: fmtNum(inspection.setback_septic_system_ft),
  },
  {
    label: "Potential Contamination Sources",
    required: "150",
    actual: fmtText(inspection.setback_contamination),
  },
  {
    label: "Existing Wells",
    required: "150",
    actual: fmtNum(inspection.setback_active_wells_ft),
  },
  {
    label: "Critical Env. Features",
    required: "150",
    actual: fmtText(inspection.setback_critical_environment),
  },
  {
    label: "Property Line",
    required: "50",
    actual: fmtNum(inspection.setback_property_line_ft),
  },
];

// Pivot same-day WQ rows into the field sheet's stabilization grid: one row
// per parameter (dynamic — whatever was recorded), one column per sampling
// event (distinct collect_timestamp, ordered). Cell = result value ("ND" when
// flagged non-detect).
export const buildWqGrid = (waterQuality) => {
  const eventTimes = [...new Set(waterQuality.map((r) => r.collect_timestamp))];
  eventTimes.sort((a, b) => new Date(a) - new Date(b));

  const paramMap = new Map();
  waterQuality.forEach((row) => {
    if (!paramMap.has(row.parameter_ndx)) {
      paramMap.set(row.parameter_ndx, {
        parameter_ndx: row.parameter_ndx,
        label:
          row.parameter_name && row.unit_abbrev
            ? `${row.parameter_name} (${row.unit_abbrev})`
            : row.parameter_name || `Parameter ${row.parameter_ndx}`,
        display_order: row.display_order ?? 9999,
        results: new Array(eventTimes.length).fill(DASH),
      });
    }
    const param = paramMap.get(row.parameter_ndx);
    const eventIdx = eventTimes.indexOf(row.collect_timestamp);
    // Prefer the exactly-as-typed value (string, trailing zeros intact) —
    // fmtNum passes strings through untouched.
    param.results[eventIdx] = row.is_nondetect
      ? "ND"
      : fmtNum(row.result_value_entered ?? row.result_value);
  });

  const parameters = [...paramMap.values()].sort(
    (a, b) => a.display_order - b.display_order
  );

  return { eventTimes, parameters };
};

// One-line summary of shared WQ event metadata (purged / source / lab /
// equipment / sampled by), deduped across rows.
export const wqMetaSummary = (waterQuality) => {
  if (!waterQuality.length) return null;
  const uniq = (field) =>
    [...new Set(waterQuality.map((r) => r[field]).filter(Boolean))].join(", ");
  return {
    purged: yn(waterQuality.some((r) => r.well_purged)),
    source: uniq("data_source_desc") || DASH,
    lab: uniq("lab_desc") || DASH,
    equipment:
      [uniq("equipment_desc"), uniq("equipment_other_desc")]
        .filter(Boolean)
        .join(" — ") || DASH,
    sampledBy: uniq("sampled_by") || DASH,
    notes: uniq("notes") || null,
  };
};

export const reportFileName = (inspection, ext) => {
  const wellId = inspection.district_well_id || `well-${inspection.well_ndx}`;
  const date = String(inspection.inspection_date).slice(0, 10);
  return `inspection-field-sheet_${wellId}_${date}.${ext}`;
};

export const reportTitle = (inspection) =>
  `${inspection.ir_type_desc || "Inspection"} Field Sheet`;

// ---------------------------------------------------------------------------
// Blank template support — a printable/downloadable empty field sheet for
// taking on site visits.

const LINE = "______________";
const YES_NO = "Yes / No";

// Standard stabilization parameters from the paper form; the blank grid shows
// these with empty reading columns.
export const BLANK_WQ_PARAMETERS = [
  "Time",
  "Temp (degF)",
  "pH (SU)",
  "Conductivity (uS/cm)",
  "D.O. (mg/L)",
  "TDS (mg/L)",
  "Turbidity (NTU)",
  "Appearance or Smell",
];
export const BLANK_WQ_READING_COLUMNS = 6;
export const BLANK_WL_ROWS = 3;
export const BLANK_METER_ROWS = 2;

// A snapshot payload whose every display field is a fill-in line or "Yes / No"
// (the formatters pass strings through untouched). Checklist boxes stay
// unchecked; tables are rendered as empty rows by the blank-aware components.
export const blankSnapshotData = () => ({
  inspection: {
    ir_type_desc: "Post Construction Inspection",
    inspection_date: "____ / ____ / ________",
    inspected_by: LINE,
    district_well_id: LINE,
    state_well_num: LINE,
    well_name: LINE,
    well_class_name: LINE,
    well_subclass_name: null,
    well_status_desc: LINE,
    primary_use_desc: LINE,
    owner_primary_contact_name: LINE,
    owner_primary_contact_phone: LINE,
    physical_location_address: LINE,
    city: null,
    lat_dd: "N ________________  W ________________",
    sampling_spigot_desc: YES_NO,
    is_metered: YES_NO,
    meter_sn: LINE,
    meter_nickname: LINE,
    is_slab: YES_NO,
    slab_thickness_inches: LINE,
    slab_length_inches: LINE,
    slab_width_inches: LINE,
    is_steel_sleeve: YES_NO,
    sleeve_height_inches: LINE,
    sleeve_outside_diameter_inches: LINE,
    casing_type_desc: LINE,
    casing_outside_diameter_inches: LINE,
    casing_height_inches: LINE,
    surface_completion_notes: LINE,
    storage_tank: YES_NO,
    other_treatments_desc: YES_NO,
    treatments_notes: LINE,
    flow_rate_gpm: `${LINE} gpm`,
    pump_motor_size_hp: `${LINE} hP`,
    pump_type_desc: LINE,
    system_type_desc: LINE,
    pump_depth_ft: LINE,
    total_borehole_depth_ft: LINE,
    setback_septic_system_ft: "",
    setback_contamination: "",
    setback_active_wells_ft: "",
    setback_critical_environment: "",
    setback_property_line_ft: "",
    setback_notes: null,
    chk_identity: false,
    chk_location: false,
    chk_use: false,
    chk_setbacks: false,
    chk_meter: false,
    chk_equipment: false,
    chk_treatment: false,
    chk_surf_compl: false,
    chk_classification: false,
    field_notes: "",
    created_timestamp: null,
  },
  waterLevels: [],
  waterQuality: [],
  meterReadings: [],
  documents: [],
});
